<template>
  <div class="page-card">
    <div class="page-head">
      <h3>Morning Check-in Records</h3>
    </div>

    <el-form :inline="true" :model="query" size="small" class="search-form">
      <el-form-item label="Keyword">
        <el-input
          v-model="query.keyword"
          clearable
          placeholder="User ID / name / username / email"
          @keyup.enter.native="search"
        />
      </el-form-item>
      <el-form-item label="Wellness Score">
        <el-input-number v-model="query.scoreMin" :min="0" :max="100" placeholder="Min" />
        <span class="range-sep">-</span>
        <el-input-number v-model="query.scoreMax" :min="0" :max="100" placeholder="Max" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search">Search</el-button>
        <el-button @click="resetSearch">Reset</el-button>
      </el-form-item>
    </el-form>

    <el-table :data="list" v-loading="loading" border size="small">
      <el-table-column prop="id" label="ID" width="70" />
      <el-table-column label="Athlete" min-width="160" show-overflow-tooltip>
        <template slot-scope="scope">
          {{ scope.row.athleteName || scope.row.nickname || scope.row.username || '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="email" label="Email" min-width="180" show-overflow-tooltip />
      <el-table-column label="User ID" min-width="150">
        <template slot-scope="scope">{{ formatUserId(scope.row) }}</template>
      </el-table-column>
      <el-table-column prop="sleepQuality" label="Sleep" width="80" />
      <el-table-column prop="motivation" label="Motivation" width="100" />
      <el-table-column prop="stress" label="Stress" width="80" />
      <el-table-column prop="energy" label="Energy" width="80" />
      <el-table-column prop="mood" label="Mood" width="80" />
      <el-table-column prop="wellnessScore" label="WS" width="80" />
      <el-table-column label="Band" width="100">
        <template slot-scope="scope">
          <el-tag :type="bandTag(scope.row.scoreBand)" size="small">{{ scope.row.scoreBand || '-' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="createTime" label="Submitted At" min-width="160" />
      <el-table-column label="Actions" width="150" fixed="right">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="openDetail(scope.row)">Details</el-button>
          <el-button type="text" size="small" class="danger" @click="remove(scope.row)">Delete</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="pager"
      background
      layout="total, sizes, prev, pager, next"
      :total="total"
      :page-size="query.pageSize"
      :current-page="query.pageNum"
      :page-sizes="[10, 20, 50, 100]"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
    />

    <el-dialog title="Morning Check-in Details" :visible.sync="detailVisible" width="720px">
      <el-descriptions v-if="detailRecord" :column="2" size="small" border class="detail-head">
        <el-descriptions-item label="ID">{{ detailRecord.id }}</el-descriptions-item>
        <el-descriptions-item label="User ID">{{ formatUserId(detailRecord) }}</el-descriptions-item>
        <el-descriptions-item label="Athlete">{{ detailRecord.athleteName || detailRecord.nickname || detailRecord.username || '-' }}</el-descriptions-item>
        <el-descriptions-item label="Email">{{ detailRecord.email || '-' }}</el-descriptions-item>
        <el-descriptions-item label="Wellness Score">{{ detailRecord.wellnessScore }}</el-descriptions-item>
        <el-descriptions-item label="Score Band">{{ detailRecord.scoreBand || '-' }}</el-descriptions-item>
        <el-descriptions-item label="Submitted At" :span="2">{{ detailRecord.createTime }}</el-descriptions-item>
      </el-descriptions>

      <el-table :data="detailAnswers" border size="small" class="answer-table">
        <el-table-column prop="title" label="Question" min-width="220" />
        <el-table-column prop="value" label="Score" width="100" />
        <el-table-column prop="meaning" label="Meaning" min-width="240" />
      </el-table>

      <span slot="footer">
        <el-button type="primary" @click="detailVisible=false">Close</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { dmwcApi } from '@/api/health'
import { formatUserId } from '@/utils/userDisplay'

const emptyQuery = () => ({ pageNum: 1, pageSize: 10, keyword: '', scoreMin: undefined, scoreMax: undefined })

const QUESTION_META = [
  { key: 'sleepQuality', title: 'Sleep Quality', low: 'Very poor', high: 'Excellent' },
  { key: 'motivation', title: 'Motivation to Train', low: 'No desire', high: 'Highly motivated' },
  { key: 'stress', title: 'Perceived Stress', low: 'No stress', high: 'Highly stressed', inverted: true },
  { key: 'energy', title: 'Physical Energy', low: 'Exhausted', high: 'Full of energy' },
  { key: 'mood', title: 'Mood', low: 'Very low', high: 'Excellent' }
]

export default {
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      query: emptyQuery(),
      detailVisible: false,
      detailRecord: null
    }
  },
  computed: {
    detailAnswers() {
      if (!this.detailRecord) return []
      return QUESTION_META.map(item => ({
        title: item.title,
        value: this.detailRecord[item.key],
        meaning: this.answerMeaning(item, this.detailRecord[item.key])
      }))
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    formatUserId,
    async loadData() {
      this.loading = true
      try {
        const res = await dmwcApi.list(this.query)
        const data = res.data || {}
        this.list = data.records || []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    search() {
      this.query.pageNum = 1
      this.loadData()
    },
    resetSearch() {
      this.query = emptyQuery()
      this.loadData()
    },
    handleSizeChange(size) {
      this.query.pageSize = size
      this.query.pageNum = 1
      this.loadData()
    },
    handleCurrentChange(page) {
      this.query.pageNum = page
      this.loadData()
    },
    openDetail(row) {
      this.detailRecord = { ...row }
      this.detailVisible = true
    },
    answerMeaning(item, value) {
      if (value === undefined || value === null || value === '') return '-'
      const label = `${value} / 7`
      if (Number(value) <= 2) return `${label} - ${item.low}`
      if (Number(value) >= 6) return `${label} - ${item.high}`
      return `${label} - Neutral`
    },
    bandTag(band) {
      if (band === 'Ready') return 'success'
      if (band === 'Watch') return ''
      if (band === 'Caution') return 'warning'
      if (band === 'High Risk') return 'danger'
      return 'info'
    },
    async remove(row) {
      await this.$confirm(`Delete Morning Check-in record #${row.id}?`, 'Confirm', { type: 'warning' })
      await dmwcApi.remove(row.id)
      this.$message.success('Deleted successfully')
      this.loadData()
    }
  }
}
</script>

<style scoped>
.page-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.page-head h3 { margin: 0; }
.search-form { margin-bottom: 12px; }
.range-sep { display: inline-block; margin: 0 8px; color: #909399; }
.pager { margin-top: 16px; text-align: right; }
.danger { color: #f56c6c; }
.detail-head { margin-bottom: 16px; }
.answer-table { margin-top: 12px; }
</style>
